/**
 * Pricing.
 *
 * The numbers on these cards are read from src/core/plans.ts — the same module
 * the Worker enforces against. A price list maintained beside the code rather
 * than out of it is how a customer ends up paying for a limit the product does
 * not honour.
 *
 * The buy button leads to /checkout, which hands off to Polar. This page never
 * needs to know how a card works — it states a price, and the price it states is
 * the one Polar was configured with, because `npm run polar:setup` builds the
 * products from the same module.
 *
 * When the deployment is running the mock provider that says so here as well as
 * on the checkout screen. A pricing page advertising a purchase that charges
 * nothing is the exact claim this product exists to refuse.
 */

import { PLANS, PLAN_ORDER, type Plan, type PlanId } from '../../core/plans';
import type { CheckoutMode } from '../api';
import { MERCHANT_OF_RECORD } from '../../core/company';
import { link } from '../router';
import { Button } from '../ui';
import { CheckIcon, Icon, IconHover } from '../icons';
import { SiteFooter, SiteHeader } from './Chrome';

function priceLabel(plan: Plan): string {
  return plan.price === 0 ? 'Free' : `$${plan.price}`;
}

export function PlanCard({
  plan,
  current,
  highlighted,
  action,
}: {
  plan: Plan;
  current?: boolean;
  highlighted?: boolean;
  action?: React.ReactNode;
}) {
  return (
    <IconHover
      className={`card flex flex-col p-6 ${
        highlighted ? 'border-accent ring-1 ring-accent' : ''
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-[16px] font-medium">{plan.name}</h2>
        {current ? (
          <span className="chip chip-accent">Your plan</span>
        ) : highlighted ? (
          <span className="chip chip-accent">Most chosen</span>
        ) : null}
      </div>

      <p className="mt-1 min-h-[2.6rem] text-[13px] leading-relaxed text-muted">{plan.tagline}</p>

      <div className="mt-4 flex items-baseline gap-1.5">
        <span className="text-[30px] font-medium tracking-tight">{priceLabel(plan)}</span>
        {plan.price > 0 ? <span className="text-[13px] text-muted">/month</span> : null}
      </div>

      <ul className="mt-5 flex-1 space-y-2.5">
        {plan.features.map((feature) => (
          <li key={feature} className="flex items-start gap-2 text-[13.5px] leading-relaxed">
            <span className="mt-0.5 shrink-0 text-accent">
              <Icon icon={CheckIcon} size={14} isAnimated={false} />
            </span>
            <span className="text-muted">{feature}</span>
          </li>
        ))}
      </ul>

      {action ? <div className="mt-6">{action}</div> : null}
    </IconHover>
  );
}

export function PricingView({
  currentPlan,
  checkout = 'polar',
}: {
  currentPlan?: PlanId;
  /**
   * Defaults to the live provider rather than to "off" so that a page rendered
   * before /api/me has answered does not flash a notice saying the product is
   * not for sale. Only `mock` changes what this page claims.
   */
  checkout?: CheckoutMode;
}) {
  const signedIn = Boolean(currentPlan);

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader signedIn={signedIn} showPricing={false} />

      <main className="mx-auto w-full max-w-5xl flex-1 px-5 py-16">
        <div className="max-w-2xl">
          <h1 className="text-[34px] leading-tight">Pricing</h1>
          <p className="mt-3 text-[15px] leading-relaxed text-muted">
            Every limit below is one the product actually counts, enforced in the same module the
            interface reads. You bring your own model key, so nothing here is a markup on tokens —
            drafting costs you what your provider charges and nothing more.
          </p>
        </div>

        {checkout === 'mock' ? (
          <div className="mt-8 max-w-2xl rounded-lg border border-line bg-warn-wash px-4 py-3 text-[13.5px] leading-relaxed text-warn">
            <b className="font-medium">This deployment charges nothing.</b> It is running the mock
            payment provider, so choosing a plan switches it on without taking any money. The
            checkout screen says so again before you confirm.
          </div>
        ) : null}

        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {PLAN_ORDER.map((id) => {
            const plan = PLANS[id];
            const isCurrent = currentPlan === id;
            return (
              <PlanCard
                key={id}
                plan={plan}
                current={isCurrent}
                highlighted={!isCurrent && id === 'team'}
                action={
                  isCurrent ? (
                    <Button className="w-full" disabled>
                      Current plan
                    </Button>
                  ) : plan.price === 0 ? (
                    signedIn ? null : (
                      <a {...link('/signup')} className="block">
                        <Button variant="secondary" className="w-full">
                          Start free
                        </Button>
                      </a>
                    )
                  ) : signedIn ? (
                    <a {...link(`/checkout/${id}`)} className="block">
                      <Button variant={id === 'team' ? 'primary' : 'secondary'} className="w-full">
                        Choose {plan.name}
                      </Button>
                    </a>
                  ) : (
                    <a {...link('/signup')} className="block">
                      <Button variant={id === 'team' ? 'primary' : 'secondary'} className="w-full">
                        Start free, then upgrade
                      </Button>
                    </a>
                  )
                }
              />
            );
          })}
        </div>

        <section className="mt-12 grid gap-8 border-t border-line pt-10 md:grid-cols-2">
          <div>
            <h2 className="text-[16px] font-medium">Billing, cancellation and refunds</h2>
            <p className="mt-2 text-[13.5px] leading-relaxed text-muted">
              Monthly, in {PLANS.team.currency}, renewing until you stop it. Cancelling takes one
              click in Settings and keeps the plan running to the end of the period you have already
              paid for. Your first payment is fully refundable for 14 days — see{' '}
              <a {...link('/refunds')} className="text-accent underline underline-offset-2">
                refunds and cancellation
              </a>
              , which is a page rather than a paragraph because it has real answers in it.
            </p>
            <p className="mt-3 text-[13.5px] leading-relaxed text-muted">
              {MERCHANT_OF_RECORD.name} is our {MERCHANT_OF_RECORD.role}: they take the payment,
              issue your invoice and handle sales tax and VAT. Charges show up as{' '}
              <span className="text-ink">{MERCHANT_OF_RECORD.statementHint}</span>.
            </p>
          </div>
          <div>
            <h2 className="text-[16px] font-medium">Why the paid plans have no questionnaire cap</h2>
            <p className="mt-2 text-[13.5px] leading-relaxed text-muted">
              Because reviews arrive in bursts, and a monthly limit would appear in the middle of a
              live deal — which is the one moment this product exists to get you through. What is
              metered is what grows slowly: evidence cards and trust page documents. Drafting is
              uncapped because your own model key pays for it.
            </p>
          </div>
        </section>

        <p className="mt-8 max-w-2xl text-[12.5px] leading-relaxed text-faint">
          No seats, because there is one login per company today. No SSO, no priority support tier.
          When those exist they will appear here with a price; until then they are not quietly
          implied by a blank column in a comparison table.
        </p>
      </main>

      <SiteFooter />
    </div>
  );
}
